import React, { useEffect } from "react";
import { Header } from "../../Header";
import { Button } from "@learningblocks/shared-components";
import { Modal } from "@learningblocks/shared-components/lib/Modal";
import { useDispatch } from "react-redux";
import { Content } from "./Contents";
import {
  absorbanceTableCellUpdateAction,
  useSpectrophotometer,
  setWavelengthAction,
  powerToggleAction,
  zeroAction,
  choseWavelengthMatchedAction,
  recalculateAbsorbance,
  absorbanceUpdated,
} from "../store";

import cname from "../style.module.scss";
import "./style.scss";
// Update the import path below to the correct location of useSimulation
import { useSimulation } from "@learningblocks/simulation-engine";
import { questionTableCellChange } from "@learningblocks/simulations/shared-components/lib/Questions/QuestionTableRedux/store/actions";

/**
 * Documentation for SpectrophotometerConfigModalProps
 *
 * @prop isOpen - Boolean to control the visibility of the modal window.
 * @type boolean
 */
/**
 * @prop onClose - Callback function to be executed when the modal is closed.
 * @type () => void
 */
/**
 * @prop notebookCollapsed - Boolean indicating whether the notebook is collapsed.
 * @type boolean
 */
/**
 * @prop cuvetteId - The ID of the cuvette.
 * @type number
 */
/**
 * @prop size - The size of the modal window ('sm' |'md' | 'lg').
 * @type string
 */

/**
 * Props for the SpectrophotometerConfigModal component
 */
interface SpectrophotometerConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  notebookCollapsed: boolean;
  size?: "sm" | "md" | "lg";
}

/**
 * SpectrophotometerConfigModal
 *  - The modal window where we can set reference absorbance value
 *    and then view the relative absorbance values
 */
export function SpectrophotometerConfigModal(
  props: SpectrophotometerConfigModalProps
) {
  const dispatch = useDispatch();
  const {
    absorbanceTable,
    poweredUp,
    absorbanceValue,
    relativeAbsorbance,
    wavelength,
    chosenWavelength,
  } = useSpectrophotometer();
  const simState = useSimulation() as {
    cuvetteId: string;
    hasDilutedCuvette: boolean;
    absorbance: number;
    hasCuvette: boolean;
  };
  useEffect(() => {
    if (simState.hasCuvette) {
      dispatch(
        recalculateAbsorbance({
          cuvetteId: simState.cuvetteId,
          diluted: simState.hasDilutedCuvette,
        })
      );
    }
  }, [simState.cuvetteId, simState.hasDilutedCuvette]);

  useEffect(() => {
    dispatch(absorbanceUpdated(absorbanceValue));
  }, [absorbanceValue]);

  useEffect(() => {
    dispatch(absorbanceUpdated(simState.absorbance));
  }, [simState.absorbance]);

  const saveAbsorbanceValuesAndClose = () => {
    absorbanceTable.data.forEach((row) => {
      row.forEach((cell) => {
        if (cell.linkedField && cell.value) {
          const floatVal = parseFloat(cell.value as string);
          const answer = Math.round((2 * floatVal) / 0.017557142) / 2;
          const correction = Math.min(
            Math.max(100 * (floatVal - 0.96), 0),
            1.5
          );
          const threshold = 1;
          dispatch(
            questionTableCellChange({
              value: "",
              id: `${cell.linkedField.rowId - 1}_${cell.linkedField.cellId}`,
              componentID: cell.linkedField.componentId,

              answer: {
                min: answer + correction - threshold,
                max: answer + correction + threshold,
              },
            })
          );
          dispatch(
            questionTableCellChange({
              id: `${cell.linkedField.rowId - 1}_${cell.linkedField.cellId - 1}`,
              componentID: cell.linkedField.componentId,
              value: cell.value,
              disabled: true,
              answer: cell.value,
            })
          );
        }
      });
    });

    props.onClose();
  };

  const onCellUpdate = ({ id, value }: { id: string; value: string }) => {
    const [row, column] = id.split("_").map((id) => parseInt(id));
    dispatch(absorbanceTableCellUpdateAction({ row, column, value }));
  };

  const onWavelengthChange = (newWavelength: number) => {
    dispatch(
      setWavelengthAction({
        wavelength: newWavelength,
        cuvetteId: simState.cuvetteId,
        diluted: simState.hasDilutedCuvette,
      })
    );
    if (chosenWavelength === newWavelength) {
      dispatch(choseWavelengthMatchedAction());
    }
  };

  return (
    <Modal
      isOpen={props.isOpen}
      closeButton={true}
      close={saveAbsorbanceValuesAndClose}
      notebookExpanded={!props.notebookCollapsed}
      className={`${cname.configModal} spectrophotometer_configModal`}
    >
      <Header
        className={cname.header}
        label="SPECTROPHOTOMETER SETTINGS"
        size={props.size}
      />
      <div className={cname.section}>
        <Content
          poweredUp={poweredUp}
          wavelength={wavelength}
          setWavelength={onWavelengthChange}
          absorbanceValue={simState.absorbance}
          onClickZeroButton={() => dispatch(zeroAction())}
          relativeAbsorbance={relativeAbsorbance}
          onClickPowerButton={() => dispatch(powerToggleAction())}
          cuvetteId={simState.cuvetteId}
          absorbanceTableCellUpdate={onCellUpdate}
          absorbanceTable={absorbanceTable}
        />
      </div>
      <div className={cname.footer}>
        <Button onClick={saveAbsorbanceValuesAndClose}>Done</Button>
      </div>
    </Modal>
  );
}

SpectrophotometerConfigModal.defaultProps = {
  size: "lg",
  isOpen: false,
};
